import {
  applyDraftStateAfterInit,
  resolveDraftGateBeforeLoad,
  wireDraftAutoSave,
} from '@theme/pb-draft-persistence';
import {
  closeBuilderLoadingShell,
  ensureBuilderForProduct,
  ensureFoundationModulesReady,
  ensureTagModulesReady,
  installCloseResumeHook,
  openBuilderLoadingShell,
  suspendCartDrawerForBuilder,
  waitForBuilderReady,
  wasLoadingShellAborted,
  wireLazyBuilderChrome,
  applyFreshSessionState,
} from '@theme/pb-builder-launch';
import {
  applyEntryManifest,
  createCollectionCardEntryManifest,
  createDraftResumeEntryManifest,
  createPdpEntryManifest,
  isChildLineCartEditManifest,
  isMainCartEditManifest,
  readEntryManifest,
} from '@theme/pb-entry-manifest';
import { ensureModulesReadyForEntryManifest } from '@theme/pb-entry-readiness';
import { releaseContainment } from '@theme/pb-focus-containment';

const MODAL_MEASURABLE_TIMEOUT_MS = 3000;
const MOBILE_OPEN_CHROME_WAIT_MS = 900;

/** Runtime resolve — avoids ESM link failure when launch asset is briefly stale on CDN. */
async function ensureBuilderInitDepsReady() {
  if (typeof window.__pbEnsureBuilderInitDepsReady === 'function') {
    return window.__pbEnsureBuilderInitDepsReady();
  }
  const launch = await import('@theme/pb-builder-launch');
  if (typeof launch.ensureBuilderInitDepsReady === 'function') {
    return launch.ensureBuilderInitDepsReady();
  }
  if (typeof launch.ensureFoundationModulesReady === 'function') {
    return launch.ensureFoundationModulesReady();
  }
}

export function isMobileBuilderViewport() {
  return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
}

/**
 * Mobile cart edit: if slide transition stalls after cart drawer close, force open chrome
 * so SUB child-line edit is visible (avoids persistent white blank sheet).
 * @param {object} [pb]
 */
export async function ensureMobileBuilderOpenChrome(pb) {
  // PBFOCUS-1: this rebinds every piece of open chrome except focus containment, and the
  // clearModalOpenPending() below would cancel a pending completeOpenChrome. Safe only
  // because the one caller is cart edit on mobile, where pb.open({ instant: true }) has
  // already run completeOpenChrome synchronously — so containment is active by now. Any
  // new caller that does not open instantly must activate containment itself.
  if (!isMobileBuilderViewport()) return;

  const modal = document.getElementById('builder-modal');
  if (!modal || modal.style.display === 'none') return;

  const deadline = Date.now() + MOBILE_OPEN_CHROME_WAIT_MS;
  while (Date.now() < deadline && !document.body.classList.contains('builder-open')) {
    await new Promise((resolve) => setTimeout(resolve, 40));
  }

  modal.style.display = 'block';
  modal.classList.add('open');

  if (!document.body.classList.contains('builder-open')) {
    document.body.classList.remove('builder-closing');
    document.body.classList.add('builder-open');
  }

  const builder = pb || window.productBuilder;
  if (!builder) return;

  if (typeof builder.clearModalOpenPending === 'function') builder.clearModalOpenPending();
  if (typeof builder.syncKeyboardInset === 'function') builder.syncKeyboardInset();
  if (typeof builder.enableMobileKeyboardLayout === 'function') builder.enableMobileKeyboardLayout();
  if (typeof builder.bindMobileKeyboardChromeSync === 'function') builder.bindMobileKeyboardChromeSync();
  if (typeof builder.bindFocusTrap === 'function') builder.bindFocusTrap();
  if (typeof builder.bindButtonLayoutResizeObserver === 'function') builder.bindButtonLayoutResizeObserver();
  if (typeof builder.reconcileStepDom === 'function') builder.reconcileStepDom();

  const content = modal.querySelector('.pb-modal-content');
  if (content) content.style.transform = 'translateY(0)';

  syncBuilderChromeForViewport(builder);
}

/**
 * Re-sync modal sheet + mobile layout when viewport crosses mobile/desktop or after cart edit open.
 * @param {object} [pb]
 */
export function syncBuilderChromeForViewport(pb) {
  if (typeof document === 'undefined') return;

  const modal = document.getElementById('builder-modal');
  const root = document.getElementById('product-builder-root');
  if (!modal) return;

  const mobile = isMobileBuilderViewport();
  const bodyActive =
    document.body.classList.contains('builder-open') ||
    document.body.classList.contains('builder-closing');
  const modalVisible = modal.style.display !== 'none';

  if (!bodyActive && !modalVisible) return;

  const builder = pb || window.productBuilder;

  if (mobile && (bodyActive || modalVisible)) {
    modal.style.display = 'block';
    modal.classList.add('open');
    document.body.classList.remove('builder-closing');
    document.body.classList.add('builder-open');

    const content = modal.querySelector('.pb-modal-content');
    if (content) content.style.transform = 'translateY(0)';

    const s = window.builderState;
    if (builder && s?.cartEdit) {
      const manifest = readEntryManifest(s);
      const childManifest = isChildLineCartEditManifest(manifest);
      const mainManifest = isMainCartEditManifest(manifest);
      if (
        childManifest &&
        s.cartEdit &&
        (s.cartEdit.isAddon || s.cartEdit.isBundleRow) &&
        typeof builder.runChildCartEditPresentationPass === 'function'
      ) {
        builder.runChildCartEditPresentationPass();
      } else if (
        (mainManifest || (!childManifest && s.mode === 'MAIN')) &&
        window.PBCartEditPresenter &&
        typeof window.PBCartEditPresenter.runMainCartEditPresentationPass === 'function'
      ) {
        window.PBCartEditPresenter.runMainCartEditPresentationPass(builder);
      }
    }

    if (builder?.reconcileStepDom) builder.reconcileStepDom();
    if (builder?.enableMobileKeyboardLayout) builder.enableMobileKeyboardLayout();
    if (builder?.syncKeyboardInset) builder.syncKeyboardInset();
    return;
  }

  if (!mobile && root) {
    root.style.removeProperty('--pb-kb-inset');
    // MKB-4: the crop goes with the inset. Both are corrections for a phone keyboard and
    // neither has any meaning above the breakpoint — and this is the one place in the tree
    // where the crop's cleanup could disagree with its writer.
    root.style.removeProperty('--pb-kb-crop');
    document.body.classList.remove('pb-keyboard-open');
    root.classList.remove('pb-mobile-keyboard-layout');
  }
}

let viewportBreakpointSyncWired = false;

/** @param {object} [pb] */
export function wireViewportBreakpointSync(pb) {
  if (typeof window === 'undefined' || viewportBreakpointSyncWired) return;
  viewportBreakpointSyncWired = true;

  const handler = () => {
    if (
      !document.body.classList.contains('builder-open') &&
      !document.body.classList.contains('builder-closing')
    ) {
      return;
    }
    syncBuilderChromeForViewport(pb || window.productBuilder);
  };

  const mq = window.matchMedia('(max-width: 767px)');
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(handler);
  }
  window.addEventListener('resize', handler);
}

/**
 * @param {object} [pb]
 */
export function presentChildLineCartEditSubFlow(pb) {
  const mainCont = document.getElementById('main-flow-container');
  const subCont = document.getElementById('sub-flow-container');
  if (mainCont) mainCont.style.display = 'none';
  if (subCont) subCont.style.display = 'flex';
  pb?.updateUI?.();
  pb?._scheduleButtonLayoutRetry?.('#sub-flow-container');
  if (typeof pb?.checkStepValidity === 'function') pb.checkStepValidity();
  if (window.PBAiImage?.init) window.PBAiImage.init('#sub-flow-container');
  if (typeof pb?.rehydrateActiveCustomField === 'function') pb.rehydrateActiveCustomField();
  if (typeof pb?.reconcileStepDom === 'function') pb.reconcileStepDom();
}

/**
 * MKB-0: load the keyboard readout, and only for someone who asked for it.
 *
 * The cheap half of the gate lives here so a shopper never fetches the module's bytes; the
 * module's own `shouldEnable` is the authority and re-checks properly (it also latches the
 * flag past Shopify's preview redirect, which drops extra query params). A false positive
 * here costs one small import that then does nothing — a false negative would cost the CEO
 * a round trip, so this errs wide on purpose.
 *
 * Delete with the rest of the readout when the MKB programme closes.
 */
if (typeof window !== 'undefined') {
  let wanted = false;
  try {
    wanted =
      String(window.location.search || '').indexOf('pb-kb-debug') !== -1 ||
      window.sessionStorage.getItem('pb-kb-debug') === '1';
  } catch (e) {
    wanted = false;
  }
  if (wanted) import('@theme/pb-kb-debug').catch(() => {});
}

export function recoverBuilderScrollLock() {
  if (typeof document === 'undefined') return;
  // PBFOCUS-1: a page left inert by a crashed session would be unusable — always release.
  releaseContainment(document);
  document.body.classList.remove('builder-closing', 'builder-open');
  document.documentElement.removeAttribute('scroll-lock');
  // MTFK-F/I: the saved scroll rides in the lock itself (top: -Ypx) — on the body's inline
  // style, and since a pop-up can wipe that (MTFK-I), also as --pb-scroll-lock-top on the
  // root element. Read it back BEFORE clearing, so a crashed open does not dump the
  // shopper at the top of the page — clearing the lock at scroll 0 is a silent teleport.
  const inlineTop = parseInt(document.body.style.top, 10) || 0;
  const varTop = parseInt(document.documentElement.style.getPropertyValue('--pb-scroll-lock-top'), 10) || 0;
  const lockedTop = inlineTop < 0 ? inlineTop : varTop;
  document.documentElement.style.removeProperty('--pb-scroll-lock-top');
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  if (lockedTop < 0) window.scrollTo({ top: -lockedTop, behavior: 'instant' });
  // And the chrome's own flag must not survive the recovery: a stale "locked" would make
  // the next open trust it and run with the document unlocked — the exact iOS
  // displacement the lock exists to prevent — then restore a stale scroll on close.
  const chrome = typeof window !== 'undefined' ? window.productBuilder : null;
  if (chrome) {
    chrome._pbScrollLocked = false;
    chrome._pbScrollLockY = 0;
  }
}

/**
 * Boot safety net — clear stuck modal chrome when no cart edit session is active.
 * Prevents blank white page after a failed cart Edit open + refresh/navigation.
 */
export function recoverStaleBuilderChrome() {
  if (typeof document === 'undefined') return;
  const s = window.builderState;
  const modal = document.getElementById('builder-modal');
  const modalVisible = modal && modal.style.display !== 'none' && modal.classList.contains('open');
  const bodyLocked =
    document.body.classList.contains('builder-open') ||
    document.body.classList.contains('builder-closing');
  const cartEditActive = !!(s && s.cartEdit);

  if (!bodyLocked && !modalVisible) return;
  if (cartEditActive && modalVisible) return;

  recoverBuilderScrollLock();
  if (modal) {
    modal.style.display = 'none';
    modal.classList.remove('open');
  }
  const mainCont = document.getElementById('main-flow-container');
  const subCont = document.getElementById('sub-flow-container');
  if (mainCont) mainCont.style.display = '';
  if (subCont) subCont.style.display = '';
}

export function isBuilderModalMeasurable() {
  const modal = document.getElementById('builder-modal');
  if (!modal) return false;
  return modal.style.display !== 'none';
}

export function waitForBuilderModalMeasurable(timeoutMs = MODAL_MEASURABLE_TIMEOUT_MS) {
  if (isBuilderModalMeasurable()) return Promise.resolve();
  const deadline = Date.now() + timeoutMs;
  return new Promise((resolve) => {
    function tick() {
      if (isBuilderModalMeasurable() || Date.now() > deadline) {
        resolve();
        return;
      }
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(tick);
      } else {
        setTimeout(tick, 16);
      }
    }
    tick();
  });
}

/**
 * Canonical builder open sequence for PDP, collection card, and cart edit.
 * @param {object} [options]
 * @param {'pdp'|'card'|'cartEdit'} [options.entry]
 * @param {string} [options.handle]
 * @param {string|number} [options.productId]
 * @param {boolean} [options.freshSession]
 * @param {boolean} [options.loadingShell]
 * @param {boolean} [options.suspendCartDrawer] Close cart drawer before open (required — native dialog top layer blocks clicks)
 * @param {(pb: object) => void|Promise<void>} [options.beforePresent]
 * @param {(pb: object) => void|Promise<void>} [options.afterPresent]
 */
export function ensureChildLineCartEditReady(pb) {
  presentChildLineCartEditSubFlow(pb);
  pb?.reconcileStepDom?.();

  const body = document.getElementById('sub-builder-body');
  const controls = document.getElementById('sub-builder-controls');
  let stepCount =
    (body?.querySelectorAll('.pb-step').length || 0) +
    (controls?.querySelectorAll('.pb-step').length || 0);

  if (stepCount < 1 && pb) {
    const ensureReady = window.PBCartEditPresenter?.ensureSubCartEditReady;
    if (typeof ensureReady === 'function') {
      stepCount = ensureReady(pb);
    }
  }

  if (stepCount < 1) {
    throw new Error('Editor could not load. Please close and try again.');
  }
}

/** @deprecated Use ensureChildLineCartEditReady — kept for tests */
export function assertChildLineCartEditVisible() {
  ensureChildLineCartEditReady(window.productBuilder);
}

/**
 * Mount SUB child-line cart edit steps before modal opens (PB-AOG-12).
 * @param {object} pb
 */
export async function presentChildCartEditBeforeOpen(pb) {
  const presentPending =
    typeof window.__pbPresentPendingChildLineCartEdit === 'function'
      ? window.__pbPresentPendingChildLineCartEdit
      : null;
  if (window.builderState?._pendingAddonCartPresent && !window.builderState?._addonCartSubPresented) {
    if (presentPending) {
      await presentPending(pb);
      return;
    }
    if (typeof window.PBCartEditPresenter?.ensureSubCartEditReady === 'function') {
      window.PBCartEditPresenter.ensureSubCartEditReady(pb);
      return;
    }
  }
  presentChildLineCartEditSubFlow(pb);
  ensureChildLineCartEditReady(pb);
}

/**
 * Hydrate MAIN cart edit fields before modal opens (PB-AOG-12).
 * @param {object} pb
 */
export async function presentMainCartEditBeforeOpen(pb) {
  if (
    window.PBCartEditPresenter &&
    typeof window.PBCartEditPresenter.runMainCartEditPresentationPass === 'function'
  ) {
    window.PBCartEditPresenter.runMainCartEditPresentationPass(pb);
    return;
  }
  if (typeof pb.runMainCartEditPresentationPass === 'function') {
    pb.runMainCartEditPresentationPass();
  }
}

/**
 * @param {object} [options]
 */
export async function bootBuilderOpen(options = {}) {
  const {
    entry = 'pdp',
    handle,
    productId,
    freshSession = false,
    loadingShell = false,
    suspendCartDrawer = false,
    beforePresent,
    afterPresent,
    resumeDraft,
    promptResume,
  } = options;

  const phase = name => options.onPhase?.(name);
  const resolvedProductId = productId ?? window.BUILDER_DATA?.mainProduct?.id;
  const isCartEdit = entry === 'cartEdit';
  const targetHandle = handle || window.BUILDER_DATA?.mainProduct?.handle;
  const giftContext = window.PPGiftClaimContext;
  const savedGift = !isCartEdit && (targetHandle
    ? giftContext?.tokenForProduct(targetHandle)
    : entry === 'pdp' && giftContext?.tokenForPage());
  if (savedGift && window.PPGiftClaim?.active !== true) {
    if (giftContext.tokenForPage?.()) {
      throw new Error('Your prepaid gift is still loading. Please try again in a moment.');
    }
    window.location.assign(giftContext.urlForProduct(targetHandle));
    return;
  }
  // Cart guards and third-party cards also open with entry:'card'. The paid
  // entitlement belongs to the product, not to the button that opened it.
  const sameMountedProduct = resolvedProductId != null &&
    String(resolvedProductId) === String(window.BUILDER_DATA?.mainProduct?.id);
  const adapterMatches = window.PPGiftClaim?.matchesProduct?.(targetHandle);
  const isGiftClaim = !isCartEdit && window.PPGiftClaim?.active === true &&
    (Boolean(savedGift) || (adapterMatches ?? (entry === 'pdp' || sameMountedProduct)));
  window.PPGiftClaim?.releaseBuilder?.();

  if (!isCartEdit && typeof window !== 'undefined' && window.builderState?.cartEdit) {
    const s = window.builderState;
    s.cartEdit = null;
    delete s._addonCartSubPresented;
    delete s._pendingAddonCartPresent;
    if (s.mode === 'SUB') s.mode = 'MAIN';
  }

  let effectiveFreshSession = freshSession;
  let pendingDraft = null;

  // Phase 1 — draft gate (localStorage + prompt only; no loading shell or section fetch)
  if (resumeDraft && !isCartEdit && !isGiftClaim) {
    if (String(resumeDraft.productId) !== String(resolvedProductId)) throw new Error('Saved personalization belongs to another product');
    pendingDraft = resumeDraft;
  } else if (resolvedProductId != null && !isCartEdit && !isGiftClaim) {
    const gate = await resolveDraftGateBeforeLoad(resolvedProductId, {
      handle: targetHandle,
      freshSession,
      cartEdit: isCartEdit,
      applyFreshSessionState,
      promptResume,
    });
    if (gate.aborted) return;
    if (gate.freshSession) effectiveFreshSession = true;
    pendingDraft = gate.draft;
  }

  if (loadingShell) openBuilderLoadingShell();

  try {
    if (entry === 'card' || entry === 'cartEdit') {
      if (!handle || productId == null || productId === '') {
        throw new Error('Missing product handle or id');
      }
      await ensureBuilderForProduct(handle, productId, {
        freshSession: effectiveFreshSession,
        skipDraftPrompt: true,
        pendingDraft,
      });
      if (wasLoadingShellAborted()) return;
    } else {
      await ensureFoundationModulesReady();
      await ensureTagModulesReady();
      if (!window.productBuilder?.ready) {
        await waitForBuilderReady();
      }
    }

    phase('product');
    installCloseResumeHook();
    wireLazyBuilderChrome();

    await ensureTagModulesReady();

    const pb = window.productBuilder;
    if (!pb) throw new Error('Builder failed to load');
    // Each native entry owns its completion; a landing popup installs save-only below.
    delete pb.landingSaveOnly;
    delete pb._experienceSurface;

    await ensureFoundationModulesReady();

    const inherit = window.PBShopifyBundleInherit;
    if (
      window.BUILDER_DATA?.shopifyBundle === true &&
      inherit &&
      typeof inherit.ensureShopifyBundleReady === 'function'
    ) {
      await inherit.ensureShopifyBundleReady();
    }

    phase('foundation');
    if (typeof beforePresent === 'function') {
      await beforePresent(pb);
    }

    let draftHydration = null;

    // PDP + collection card share draft Continue: applyDraftStateAfterInit + draft_resume manifest
    if (isGiftClaim) {
      delete window.__pbPendingDraftHydration;
      applyEntryManifest(window.builderState, window.PPGiftClaim.prepareBuilder(pb));
    } else if (pendingDraft && (entry === 'pdp' || entry === 'card')) {
      if (typeof window !== 'undefined' && window.__pbPendingDraftHydration) {
        draftHydration = window.__pbPendingDraftHydration;
        delete window.__pbPendingDraftHydration;
      } else {
        draftHydration = applyDraftStateAfterInit(pendingDraft);
      }
      applyEntryManifest(
        window.builderState,
        createDraftResumeEntryManifest({
          handle,
          productId: resolvedProductId,
          mode: window.builderState?.mode,
        })
      );
    } else if (typeof window !== 'undefined' && window.__pbPendingDraftHydration) {
      draftHydration = window.__pbPendingDraftHydration;
      delete window.__pbPendingDraftHydration;
      applyEntryManifest(
        window.builderState,
        createDraftResumeEntryManifest({
          handle,
          productId: resolvedProductId,
          mode: window.builderState?.mode,
        })
      );
    } else if (entry === 'card') {
      applyEntryManifest(
        window.builderState,
        createCollectionCardEntryManifest({ handle, productId: resolvedProductId })
      );
    } else if (entry === 'pdp') {
      applyEntryManifest(
        window.builderState,
        createPdpEntryManifest({ freshSession: effectiveFreshSession, productId: resolvedProductId })
      );
    }

    phase('hydration');
    if (resolvedProductId != null) {
      wireDraftAutoSave(resolvedProductId);
    }

    if (loadingShell) closeBuilderLoadingShell();

    if (typeof afterPresent === 'function') {
      await afterPresent(pb);
    }

    const entryManifest = readEntryManifest(window.builderState);
    const isChildCartSubEdit = isChildLineCartEditManifest(entryManifest);
    const isMainCartEdit = isMainCartEditManifest(entryManifest);
    const instantCartEditOpen = isCartEdit && isMobileBuilderViewport();
    // The mounted shared surface already owns its slide-in. Replaying the native
    // modal animation delays its controls and makes a panel transition feel like two opens.
    const instantSharedOpen = !!(options.experienceSurface?.isOpen?.() &&
      pb._experienceSurface === options.experienceSurface);

    if (suspendCartDrawer) {
      await suspendCartDrawerForBuilder();
    }

    // Cart edit: mount editor steps before modal visible — prevents blank sheet (PB-AOG-12).
    if (isCartEdit && (isChildCartSubEdit || isMainCartEdit)) {
      await ensureBuilderInitDepsReady();
      if (isChildCartSubEdit) {
        await presentChildCartEditBeforeOpen(pb);
      } else {
        await presentMainCartEditBeforeOpen(pb);
      }
    }

    if (typeof pb.open === 'function') {
      pb.open({ instant: instantCartEditOpen || instantSharedOpen });
    } else {
      pb.open();
    }

    phase('opened');
    await waitForBuilderModalMeasurable();

    if (isCartEdit && isMobileBuilderViewport()) {
      await ensureMobileBuilderOpenChrome(pb);
    }

    if (isCartEdit || document.body.classList.contains('builder-open')) {
      syncBuilderChromeForViewport(pb);
    }

    if (isChildCartSubEdit) {
      if (typeof pb.runChildCartEditPresentationPass === 'function') {
        pb.runChildCartEditPresentationPass();
      } else {
        presentChildLineCartEditSubFlow(pb);
        ensureChildLineCartEditReady(pb);
      }
    } else if (isMainCartEdit) {
      if (typeof pb.runMainCartEditPresentationPass === 'function') {
        pb.runMainCartEditPresentationPass();
      } else if (typeof window.PBCartEditPresenter?.runMainCartEditPresentationPass === 'function') {
        window.PBCartEditPresenter.runMainCartEditPresentationPass(pb);
      }
    } else if (!isCartEdit) {
      // Collection card + PDP: full readiness before presentation (mirrors cart edit)
      await ensureModulesReadyForEntryManifest(entryManifest);
      phase('dependencies');
      if (typeof pb.runPresentationPass === 'function') {
        // draftHydration → runDraftContinuePresentationPass inside modal chrome
        pb.runPresentationPass(draftHydration ? { draftHydration } : undefined);
        phase('presentation');
      }
    }
  } catch (err) {
    if (loadingShell) closeBuilderLoadingShell();
    if (!options.experienceSurface?.isOpen()) recoverBuilderScrollLock();
    throw err;
  }
}

if (typeof window !== 'undefined') {
  window.__pbBootBuilderOpen = bootBuilderOpen;
  window.__pbIsBuilderModalMeasurable = isBuilderModalMeasurable;
  window.__pbRecoverStaleBuilderChrome = recoverStaleBuilderChrome;
  window.__pbSyncBuilderChromeForViewport = syncBuilderChromeForViewport;
  wireViewportBreakpointSync();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recoverStaleBuilderChrome, { once: true });
  } else {
    recoverStaleBuilderChrome();
  }
  document.dispatchEvent(new CustomEvent('pb:boot-ready'));
}
